import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Space, Tag, Button, Modal, Form, Input, message } from 'antd';
import { YoutubeOutlined, LinkOutlined, EditOutlined, CheckOutlined, FireOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api, { formatApiError } from '../utils/api';

// Shown at the top of every page:
// 1) Staff an admin has shown new First Time Use videos to: fill details / make Public, then Done.
// 2) Staff on the notify list: songs uploaded to a YouTube channel in the last 7 days.
export default function VideoTrackerBanner() {
  const [items, setItems] = useState([]);
  const [queue, setQueue] = useState([]);
  const [cmsQueue, setCmsQueue] = useState([]);
  const [cmsPopup, setCmsPopup] = useState([]);
  const [cmsDone, setCmsDone] = useState(null);
  const [cmsForm] = Form.useForm();
  const [pubRow, setPubRow] = useState(null);
  const [popupIds, setPopupIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchFeed = useCallback(async () => {
    try {
      const res = await api.get('/video-tracker/feed');
      setItems(res.data || []);
    } catch (err) { /* table may not exist / no access */ }
    try {
      const res = await api.get('/video-tracker/publish-queue');
      const q = Array.isArray(res.data) ? res.data : [];
      setQueue(q);
      // Pop up once per login session for every fresh video not yet marked Done.
      const seen = new Set(JSON.parse(sessionStorage.getItem('vt_fresh_seen') || '[]'));
      const fresh = q.filter(x => !seen.has(x.id)).map(x => x.id);
      if (fresh.length) setPopupIds(fresh);
    } catch (err) { /* no access */ }
    try {
      const res = await api.get('/video-tracker/cms-queue');
      const q = Array.isArray(res.data) ? res.data : [];
      setCmsQueue(q);
      const seen = new Set(JSON.parse(sessionStorage.getItem('vt_cms_seen') || '[]'));
      const fresh = q.filter(x => !seen.has(x.id)).map(x => x.id);
      if (fresh.length) setCmsPopup(fresh);
    } catch (err) { /* no access */ }
  }, []);

  const closeCmsPopup = () => {
    const seen = new Set(JSON.parse(sessionStorage.getItem('vt_cms_seen') || '[]'));
    cmsPopup.forEach(id => seen.add(id));
    sessionStorage.setItem('vt_cms_seen', JSON.stringify([...seen]));
    setCmsPopup([]);
  };
  const submitCmsDone = async () => {
    try {
      const vals = await cmsForm.validateFields();
      setSaving(true);
      await api.post(`/video-tracker/${cmsDone.song.id}/cms-done`, { channel_id: cmsDone.ch.channel_id, link: vals.link });
      message.success(`${cmsDone.ch.channel_name} par upload mark ho gaya`);
      setCmsDone(null);
      fetchFeed();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(formatApiError(err, 'Save nahi hua'));
    } finally { setSaving(false); }
  };
  const cmsCard = (it, withButtons) => (
    <div key={it.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid var(--border)' }}>
      {it.video_id && (
        <a href={it.youtube_link} target="_blank" rel="noreferrer">
          <img src={`https://i.ytimg.com/vi/${it.video_id}/mqdefault.jpg`} alt="" style={{ width: 160, borderRadius: 6 }} />
        </a>
      )}
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {it.first_time_code && <Tag color="purple">{it.first_time_code}</Tag>}
          <b>{it.song_title}</b>
          {it.singer && <Tag>{it.singer}</Tag>}
          <Tag color="red" style={{ fontWeight: 'bold' }}>SIRF {String(it.cms_scope).toUpperCase()} CHANNELS</Tag>
          {it.youtube_link && <a href={it.youtube_link} target="_blank" rel="noreferrer"><LinkOutlined /> Original</a>}
        </div>
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {it.channels.map(ch => (
            <div key={ch.channel_id} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <Tag color="red">{ch.channel_name}</Tag>
              {ch.done_at
                ? <><Tag color="green">Upload ho gaya{ch.auto ? ' (auto)' : ''}</Tag>{ch.link && <a href={ch.link} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a>}</>
                : <><Tag color="orange">Baaki</Tag>{withButtons && <Button size="small" icon={<CheckOutlined />} style={{ borderColor: '#52c41a', color: '#389e0d' }}
                    onClick={() => { cmsForm.resetFields(); setCmsDone({ song: it, ch }); }}>Upload kar diya</Button>}</>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  const cmsPopupRows = cmsQueue.filter(x => cmsPopup.includes(x.id));

  useEffect(() => {
    fetchFeed();
    const t = setInterval(() => { if (!document.hidden) fetchFeed(); }, 120000);
    return () => clearInterval(t);
  }, [fetchFeed]);

  const closePopup = () => {
    const seen = new Set(JSON.parse(sessionStorage.getItem('vt_fresh_seen') || '[]'));
    popupIds.forEach(id => seen.add(id));
    sessionStorage.setItem('vt_fresh_seen', JSON.stringify([...seen]));
    setPopupIds([]);
  };
  const popupRows = queue.filter(x => popupIds.includes(x.id));

  const openPublish = (row) => {
    setPubRow(row);
    form.setFieldsValue({ video_title: row.video_title || row.song_title || '', youtube_link: row.youtube_link || '', note: '' });
  };

  const markDone = async (row) => {
    try {
      await api.post(`/video-tracker/${row.id}/publish-done`);
      message.success('Done — list se hat gaya');
      setQueue(q => q.filter(x => x.id !== row.id));
    } catch (err) {
      message.error(formatApiError(err, 'Done nahi hua'));
    }
  };

  const submitPublish = async () => {
    try {
      const vals = await form.validateFields();
      setSaving(true);
      await api.post(`/video-tracker/${pubRow.id}/publish`, vals);
      message.success('Details save ho gayi — video Public mark ho gaya');
      setPubRow(null);
      fetchFeed();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(formatApiError(err, 'Save nahi hua'));
    } finally { setSaving(false); }
  };

  return (
    <>
      {cmsQueue.length > 0 && (
        <Alert
          type="error" showIcon icon={<YoutubeOutlined />}
          style={{ marginBottom: 16, borderRadius: 10 }}
          message={<b>Ye gaane sirf apne CMS ke channels par daalne hain — aur kisi channel par nahi ({cmsQueue.length})</b>}
          description={
            <div>
              <span style={{ color: '#8c8c8c' }}>
                Har gaane ke saamne likha hai kis CMS ke channel par daalna hai. Neeche sirf aapke channel dikh rahe hain. Upload karne ke baad "Upload kar diya" dabake us video ka link daalo (ya apne aap ✅ lag jayega). Ye card tab tak rahega jab tak aapke saare channel par upload na ho jaye.
              </span>
              {cmsQueue.map(it => cmsCard(it, true))}
            </div>
          }
        />
      )}
      {queue.length > 0 && (
        <Alert
          type="warning" showIcon icon={<YoutubeOutlined />}
          style={{ marginBottom: 16, borderRadius: 10 }}
          message={<b><FireOutlined style={{ color: '#fa541c' }} /> Fresh songs upload hue — tags, thumbnail lagao aur Public karo ({queue.length})</b>}
          description={
            <Space direction="vertical" style={{ width: '100%' }}>
              <span style={{ color: '#8c8c8c' }}>
                Tarika: YouTube Studio me video kholo → title, description, tags aur thumbnail lagao → Visibility "Public" karo → yahan "Details bharo & Public" dabake title + link save karo. Check karne ke baad "Done" dabao.
              </span>
              {queue.map(it => (
                <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {it.first_time_code && <Tag color="purple">{it.first_time_code}</Tag>}
                  <span style={{ fontWeight: 600 }}>{it.song_title}</span>
                  {it.singer && <Tag>{it.singer}</Tag>}
                  {it.channel_name && <Tag color="red">{it.channel_name}</Tag>}
                  {it.first_time_at && <Tag color="blue">{dayjs(it.first_time_at).format('DD MMM')}</Tag>}
                  {it.youtube_link && <a href={it.youtube_link} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a>}
                  {it.publish_status === 'public' && <Tag color="green">Public ho chuka</Tag>}
                  {it.shown_by_name && <span style={{ color: '#8c8c8c', fontSize: 12 }}>by {it.shown_by_name}</span>}
                  {it.publish_status !== 'public' && <Button size="small" type="primary" icon={<EditOutlined />} onClick={() => openPublish(it)}>Details bharo & Public</Button>}
                  <Button size="small" icon={<CheckOutlined />} style={{ borderColor: '#52c41a', color: '#389e0d' }} onClick={() => markDone(it)}>Done</Button>
                </div>
              ))}
            </Space>
          }
        />
      )}
      {items.length > 0 && (
        <Alert
          type="success" showIcon icon={<YoutubeOutlined />}
          style={{ marginBottom: 16, borderRadius: 10 }}
          message={<b>Naye Songs Upload Hue (last 7 days) — {items.length}</b>}
          description={
            <Space direction="vertical" style={{ width: '100%' }}>
              {items.map(it => (
                <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>{it.song_title}</span>
                  {it.channel_name && <Tag color="red">{it.channel_name}</Tag>}
                  {it.uploaded_at && <Tag color="blue">{dayjs(it.uploaded_at).format('DD MMM')}</Tag>}
                  {it.youtube_link && (
                    <a href={it.youtube_link} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a>
                  )}
                </div>
              ))}
            </Space>
          }
        />
      )}
      <Modal
        open={cmsPopupRows.length > 0 && !cmsDone && !pubRow}
        onCancel={closeCmsPopup}
        title={<span><FireOutlined style={{ color: '#fa541c' }} /> Naya gaana — sirf diye gaye CMS ke channels par daalna hai</span>}
        footer={<Button type="primary" onClick={closeCmsPopup}>Samajh gaya</Button>} width={720}
      >
        {cmsPopupRows.map(it => cmsCard(it, false))}
      </Modal>
      <Modal
        title={<span><YoutubeOutlined style={{ color: '#f00' }} /> {cmsDone?.ch?.channel_name} par upload — {cmsDone?.song?.song_title}</span>}
        open={!!cmsDone} onOk={submitCmsDone} onCancel={() => setCmsDone(null)} okText="Save" confirmLoading={saving}
      >
        <Form form={cmsForm} layout="vertical">
          <Form.Item name="link" label={`${cmsDone?.ch?.channel_name || ''} par jo video daali uska YouTube link`} rules={[{ required: true, message: 'YouTube link daalo' }]}>
            <Input placeholder="https://youtu.be/..." />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={popupRows.length > 0 && !pubRow}
        onCancel={closePopup}
        title={<span><FireOutlined style={{ color: '#fa541c' }} /> Fresh song upload hua hai — {popupRows.length}</span>}
        footer={<Button type="primary" onClick={closePopup}>Theek hai, abhi karta hoon</Button>}
      >
        <div style={{ marginBottom: 12, color: '#8c8c8c' }}>
          Ye fresh gaane aapke channel par upload hue hain. YouTube Studio me tags, description aur thumbnail lagao, phir Public karo.
          Kaam hone par upar list me "Done" tick karo — tab tak ye list dikhti rahegi.
        </div>
        {popupRows.map(it => (
          <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid var(--border)' }}>
            {it.first_time_code && <Tag color="purple">{it.first_time_code}</Tag>}
            <b>{it.song_title}</b>
            {it.singer && <Tag>{it.singer}</Tag>}
            {it.channel_name && <Tag color="red">{it.channel_name}</Tag>}
            {it.youtube_link && <a href={it.youtube_link} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a>}
            {it.publish_status !== 'public' && <Button size="small" type="primary" icon={<EditOutlined />} onClick={() => { closePopup(); openPublish(it); }}>Details bharo & Public</Button>}
          </div>
        ))}
      </Modal>
      <Modal
        title={<span><YoutubeOutlined style={{ color: '#f00' }} /> Details bharo & Public — {pubRow?.song_title}</span>}
        open={!!pubRow} onOk={submitPublish} onCancel={() => setPubRow(null)} okText="Public ho gaya — Save" confirmLoading={saving}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="video_title" label="YouTube Video Title" rules={[{ required: true, message: 'Video title daalo' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="youtube_link" label="YouTube Link" rules={[{ required: true, message: 'YouTube link daalo' }]}>
            <Input placeholder="https://youtu.be/..." />
          </Form.Item>
          <Form.Item name="note" label="Note (optional)">
            <Input placeholder="e.g. tags/description daal diye" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
